import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateTeamGroup } from "@/data/use-groups-data";
import { useNavigate } from "@tanstack/react-router";
import { ChangeEvent, useState } from "react";
import { toast } from "sonner";

type CreateTeamGroupDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationUuid: string;
};

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 23);

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,21}[a-z0-9]$/;

const validateSlug = (slug: string): string | null => {
  if (slug.length < 3) return "Slug must be at least 3 characters.";
  if (slug.length > 23) return "Slug must be at most 23 characters.";
  if (!SLUG_REGEX.test(slug))
    return "Slug must be kebab-case (lowercase letters, numbers, and hyphens only).";
  return null;
};

export const CreateTeamGroupDialog = ({
  open,
  onOpenChange,
  organizationUuid,
}: CreateTeamGroupDialogProps) => {
  const { createTeamGroup, isCreating } = useCreateTeamGroup();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);

  const handleClose = () => {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setSlugError(null);
    onOpenChange(false);
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextName = event.target.value;
    setName(nextName);
    if (!slugTouched) {
      const nextSlug = toSlug(nextName);
      setSlug(nextSlug);
      setSlugError(validateSlug(nextSlug));
    }
  };

  const handleSlugChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextSlug = event.target.value;
    setSlug(nextSlug);
    setSlugTouched(true);
    setSlugError(validateSlug(nextSlug));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateSlug(slug);
    if (error) {
      setSlugError(error);
      return;
    }

    try {
      const created = await createTeamGroup({
        name,
        slug,
        organizationUuid,
      });
      toast.success(`Workspace "${name}" created.`);
      handleClose();
      void navigate({
        to: "/w/$groupSlug",
        params: { groupSlug: created.slug },
      });
    } catch {
      toast.error("Failed to create workspace. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create team workspace</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="team-name">Name</Label>
            <Input
              id="team-name"
              value={name}
              onChange={handleNameChange}
              placeholder="Acme Team"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="team-slug">Slug</Label>
            <Input
              id="team-slug"
              value={slug}
              onChange={handleSlugChange}
              placeholder="acme-team"
            />
            {slugError && (
              <p className="text-destructive text-sm">{slugError}</p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || !name || !!slugError}>
              {isCreating ? "Creating…" : "Create workspace"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
